import { useState } from "react";
import { SizedText } from "../../styles/styles";
import { useTranslation, Trans } from "@coworker/locales";
import { useTasksByMedia } from "../../hooks/useTasksByMedia";
import {
  filterOngoingTasksByMediaId,
  getMediaTypeText,
  setOngoingTasksToClosed,
} from "../../tools";
import { useUserId } from "../../../../core/auth/useLoggedInUser";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { deleteMedia } from "../../services/medias.service";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { Media } from "../../types/media";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { MediaTypes } from "../../constants";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../hooks/queryKeys";
import {
  FixaAccordion,
  FixaAccordionItem,
} from "@coworker/apprestructured/src/shared/wrappers/FixaAccordion/FixaAccordion";

interface DeleteMediaModalProps {
  media: Media;
}

export const DeleteMediaModal = ({ media }: DeleteMediaModalProps) => {
  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const { showToast } = useToastNotification();
  const queryClient = useQueryClient();

  const userId = useUserId();
  const tasks = useTasksByMedia(media?.id || "");

  const [modalOpen, setModalOpen] = useState(false);

  const handleDeleteMedia = async () => {
    if (filterOngoingTasksByMediaId(tasks.tasks, media.id).length > 0) {
      await setOngoingTasksToClosed(
        media.id,
        t("taskClosedBecauseRemovedRoomString"),
        tasks,
        userId
      );
    }
    await deleteMedia(media.id);
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.Medias],
    });
    setModalOpen(false);
    if (media.mediaType === MediaTypes.ROOM) {
      navigate(
        `/mediamaintenance/tabs/${media.parentMediaId}?tab=article-list-tab`
      );
    } else {
      navigate("/mediamaintenance");
    }
    showToast(t("mediaDeletedString"));
  };

  if (!media) return <></>;

  return (
    <FixaAccordion size="small" padded={true}>
      <FixaAccordionItem title={t("deleteMediaString")} id="delete-media">
        <FixaButton
          text={t("deleteMediaString")}
          type="danger"
          size="small"
          onClick={() => setModalOpen(!modalOpen)}
        />
        <FixaModal
          data-testid="deleteMediaModal"
          visible={modalOpen}
          handleCloseBtn={() => setModalOpen(!modalOpen)}
        >
          <FixaPrompt
            title={""}
            titleId={`delete-${media.id}`}
            header={
              <FixaModalHeader
                title={t("deleteMediaString")}
                ariaCloseTxt="Close prompt."
              />
            }
            footer={
              <FixaModalFooter>
                <FixaButton
                  data-testid="deleteMediaModal:cancel"
                  text={t("cancelString")}
                  type="secondary"
                  onClick={() => setModalOpen(!modalOpen)}
                />
                <FixaButton
                  data-testid="deleteMediaModal:delete"
                  text={t("deleteString")}
                  type="danger"
                  onClick={handleDeleteMedia}
                />
              </FixaModalFooter>
            }
          >
            <>
              <SizedText $fontSize={16} $bold={true}>
                <Trans
                  values={{
                    media: getMediaTypeText(media.mediaType as MediaTypes),
                    item: media.name,
                  }}
                >
                  surelyDeleteMedia2String
                </Trans>
              </SizedText>
              <SizedText $fontSize={14} $bold={false}>
                <Trans>deleteMediaTasksClosedString</Trans>{" "}
                <Trans>canNotBeUndoneString</Trans>
              </SizedText>
            </>
          </FixaPrompt>
        </FixaModal>
      </FixaAccordionItem>
    </FixaAccordion>
  );
};
